import React, { useState } from 'react';
import { testRetrieval } from '../api/documents';
import PageContainer from '../components/PageContainer';

const RetrievalTest: React.FC = () => {
  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState('ar');
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleTest = async () => {
    if (!query.trim()) {
      alert('Please enter a query');
      return;
    }

    setLoading(true);
    try {
      const data = await testRetrieval(query, language);
      setResults(data);
    } catch (error: any) {
      alert('Failed to test retrieval: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer
      title="Preview answers"
      subtitle="Try a question and see what the chatbot would find in your content."
    >
      <div className="card p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="query" className="label">Query</label>
            <input
              id="query"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input-base"
              placeholder="Enter your test query..."
              onKeyPress={(e) => e.key === 'Enter' && handleTest()}
            />
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-shrink-0">
              <label htmlFor="language" className="label">Language</label>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="input-base w-auto min-w-[140px]"
              >
                <option value="ar">Arabic</option>
                <option value="en">English</option>
              </select>
            </div>

            <button onClick={handleTest} disabled={loading} className="btn-primary gap-1.5 flex-shrink-0">
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Searching...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  Preview
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {results && (
        <div className="space-y-6 animate-slide-up">
          {/* Result Summary */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-primary-800 mb-4">Result Summary</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="text-xs text-primary-500 mb-1">Query</div>
                <div className="text-sm font-medium text-primary-800 truncate">{results.query}</div>
              </div>
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="text-xs text-primary-500 mb-1">Confidence</div>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${results.above_threshold ? 'text-emerald-600' : 'text-red-600'}`}>
                    {(results.confidence * 100).toFixed(1)}%
                  </span>
                  <span className={results.above_threshold ? 'badge-success' : 'badge-error'}>
                    {results.above_threshold ? 'Above threshold' : 'Below threshold'}
                  </span>
                </div>
              </div>
              <div className="bg-primary-50 rounded-lg p-4">
                <div className="text-xs text-primary-500 mb-1">Passages found</div>
                <div className="text-lg font-bold text-primary-800">{results.chunks.length}</div>
              </div>
            </div>
          </div>

          {/* Retrieved Chunks */}
          <div>
            <h3 className="text-sm font-semibold text-primary-800 mb-3">Passages</h3>
            {results.chunks.length === 0 ? (
              <div className="card p-8 text-center">
                <svg className="w-10 h-10 mx-auto text-primary-300 mb-2" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <p className="text-sm text-primary-500">No relevant passages found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {results.chunks.map((chunk: any, idx: number) => (
                  <div key={idx} className="card p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-md bg-primary-50 text-primary-700 text-xs font-bold flex items-center justify-center">
                          {idx + 1}
                        </span>
                        <span className="text-sm font-semibold text-primary-800">{chunk.title}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-primary-700">
                          {(chunk.score * 100).toFixed(1)}%
                        </div>
                        <div className="text-xs text-primary-400">Relevance</div>
                      </div>
                    </div>
                    <div className="flex gap-3 mb-3">
                      <div className="flex items-center gap-1.5 text-xs text-primary-500">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-400"></div>
                        Keyword match: {(chunk.bm25_score * 100).toFixed(1)}%
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-primary-500">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-400"></div>
                        Meaning match: {(chunk.vector_score * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div className="text-xs text-primary-700 bg-primary-50 border border-primary-100 p-3 rounded-lg font-mono whitespace-pre-wrap leading-relaxed">
                      {chunk.text_preview}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default RetrievalTest;
